import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * `asChild` renders a `<span>` instead of a nested `<label>` — a label inside
 * a label is invalid, and `Field` uses the outer one for the association.
 */
const Label = React.forwardRef<
  HTMLLabelElement,
  React.LabelHTMLAttributes<HTMLLabelElement> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const classes = cn('block text-xs font-medium leading-none text-muted-foreground', className);
  return asChild ? (
    <span className={classes} {...props} />
  ) : (
    <label ref={ref} className={classes} {...props} />
  );
});
Label.displayName = 'Label';

/**
 * Label + control, the shape every form in this phase repeats.
 *
 * The container **is** the `<label>` when no `htmlFor` is given, so the
 * control inside is associated implicitly. §9 requires labelled controls, and
 * a visually-adjacent `<label>` with no `for` is exactly the shape that looks
 * right and reads as nothing to a screen reader.
 */
export function Field({
  label,
  hint,
  htmlFor,
  children,
  className,
}: {
  label: string;
  hint?: string;
  htmlFor?: string;
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  const hintNode =
    hint === undefined ? null : <p className="text-[11px] text-muted-foreground">{hint}</p>;

  // The hint sits OUTSIDE the label: inside, it becomes part of the control's
  // accessible name, so a screen reader announces the whole sentence every
  // time focus lands on the field.
  return htmlFor === undefined ? (
    <div className={cn('space-y-1', className)}>
      <label className="block space-y-1">
        <Label asChild>{label}</Label>
        {children}
      </label>
      {hintNode}
    </div>
  ) : (
    <div className={cn('space-y-1', className)}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hintNode}
    </div>
  );
}
